// components/HeroShowcase.js
// Landing-page social proof: an animated count-up of the indexed-lawyer total, a compact
// stats strip, and a dual-row, edge-faded marquee of the actual top-tier firms in the
// database (USA + Canada). Pure CSS animation + a rAF count-up; no deps.
import React, { useEffect, useRef, useState } from 'react';

// Easy to change to the exact verified figure.
const LAWYER_COUNT = 60000;
const FIRM_COUNT = 35;

// Real firms in the database (short, recognizable names). Order is split across the two rows.
const FIRMS_ROW_A = [
  'Kirkland & Ellis', 'Skadden', 'Davis Polk', 'Sullivan & Cromwell', 'Cravath',
  'Gibson Dunn', 'Paul, Weiss', 'Simpson Thacher', 'Debevoise & Plimpton', 'Covington & Burling',
  'Goodwin', 'Cooley', 'Milbank', 'White & Case', 'WilmerHale', 'McDermott Will & Emery',
  'K&L Gates', 'Reed Smith'
];
const FIRMS_ROW_B = [
  'Squire Patton Boggs', 'Pillsbury', 'Crowell & Moring', 'Fenwick & West', 'Orrick',
  'Nixon Peabody', 'Barnes & Thornburg', 'ArentFox Schiff', 'Taft', 'Wiley Rein',
  'Blake, Cassels & Graydon', 'McCarthy Tétrault', 'Osler', 'Torys', 'Stikeman Elliott',
  'Fasken', 'Goodmans', 'Davies Ward Phillips & Vineberg'
];

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, "Helvetica Neue", Arial, sans-serif';

function useCountUp(target, durationMs = 1500) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}

function Pill({ name }) {
  return (
    <span className="hs-pill">
      <span className="hs-dot" />
      {name}
    </span>
  );
}

function Marquee({ items, reverse, isMobile }) {
  // Duplicate the list so the -50% translate loops seamlessly.
  const doubled = [...items, ...items];
  return (
    <div className="hs-marquee">
      <div className={`hs-track${reverse ? ' rev' : ''}`} style={{ animationDuration: `${isMobile ? 38 : 48}s` }}>
        {doubled.map((name, i) => <Pill key={`${name}-${i}`} name={name} />)}
      </div>
    </div>
  );
}

export default function HeroShowcase({ isMobile }) {
  const lawyers = useCountUp(LAWYER_COUNT);
  const formatted = lawyers.toLocaleString('en-US');

  return (
    <div className="hs-wrap" style={{ fontFamily: FONT }}>
      <style>{CSS}</style>

      {/* Stats strip */}
      <div className="hs-stats">
        <div className="hs-stat">
          <div className="hs-num hs-num-accent">{formatted}+</div>
          <div className="hs-label">Lawyers indexed</div>
        </div>
        <div className="hs-divider" />
        <div className="hs-stat">
          <div className="hs-num">{FIRM_COUNT}+</div>
          <div className="hs-label">Top-tier firms</div>
        </div>
        <div className="hs-divider" />
        <div className="hs-stat">
          <div className="hs-num" style={{ fontSize: 28 }}>🌎</div>
          <div className="hs-label">USA &amp; Canada</div>
        </div>
      </div>

      <div className="hs-caption">Every major firm — from AmLaw 100 to Canada’s Seven Sisters</div>

      {/* Firm marquee */}
      <Marquee items={FIRMS_ROW_A} isMobile={isMobile} />
      <Marquee items={FIRMS_ROW_B} reverse isMobile={isMobile} />
    </div>
  );
}

const CSS = `
.hs-wrap { width: 100%; max-width: 760px; margin: 18px auto 4px; display: flex; flex-direction: column; align-items: center; }
.hs-stats { display: flex; align-items: center; justify-content: center; gap: 22px; flex-wrap: wrap; }
.hs-stat { display: flex; flex-direction: column; align-items: center; min-width: 92px; }
.hs-num { font-size: 30px; font-weight: 800; line-height: 1; color: #2b2b27; letter-spacing: -0.02em; }
.hs-num-accent { background: linear-gradient(95deg, #c2613f, #d98a5f); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.hs-label { font-size: 12px; font-weight: 600; color: #8a8a82; margin-top: 6px; letter-spacing: 0.02em; text-transform: uppercase; }
.hs-divider { width: 1px; height: 34px; background: #e3e1d8; }
.hs-caption { font-size: 13px; color: #8a8a82; margin: 14px 0 12px; text-align: center; }

.hs-marquee {
  width: 100%; overflow: hidden; padding: 4px 0;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent);
}
.hs-track { display: flex; gap: 10px; width: max-content; animation: hs-scroll 48s linear infinite; }
.hs-track.rev { animation-direction: reverse; }
.hs-marquee:hover .hs-track { animation-play-state: paused; }
@keyframes hs-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }

.hs-pill {
  display: inline-flex; align-items: center; gap: 8px; white-space: nowrap;
  padding: 8px 14px; border-radius: 999px; border: 1px solid #e6e4da;
  background: #ffffffcc; color: #44443e; font-size: 13.5px; font-weight: 600;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
.hs-dot { width: 6px; height: 6px; border-radius: 50%; background: #c2613f; flex-shrink: 0; }

@media (prefers-reduced-motion: reduce) { .hs-track { animation: none; } }
`;
